import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OrderDetailsCard.scss';
import locationIcon from '../RestaurantDetailPage/img/location.png';
import { useDeliveryAddress } from '../../address/DeliveryAddressContext';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function localTodayISO() {
  const t = new Date();
  return `${t.getFullYear()}-${pad2(t.getMonth() + 1)}-${pad2(t.getDate())}`;
}

function parseTime24(s) {
  if (!s || !/^\d{1,2}:\d{2}$/.test(s)) return { h12: 12, m: 0, ap: 'PM' };
  const [hs, ms] = s.split(':');
  let h = parseInt(hs, 10);
  const m = parseInt(ms, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return { h12: 12, m: 0, ap: 'PM' };
  h = Math.min(23, Math.max(0, h));
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { h12, m: Math.min(59, Math.max(0, m)), ap };
}

function toTime24(h12, m, ap) {
  let h = h12 % 12;
  if (ap === 'PM') h += 12;
  return `${pad2(h)}:${pad2(m)}`;
}

function normalizeTimeString(t) {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return '12:00';
  const [a, b] = t.split(':');
  return `${pad2(parseInt(a, 10))}:${pad2(parseInt(b, 10))}`;
}

function formatDateDisplay(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, mo, d] = iso.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime12From24(s) {
  const { h12, m, ap } = parseTime24(s);
  return `${h12}:${pad2(m)} ${ap}`;
}

function OdcIconCalendar() {
  return (
    <svg className="odc__dt-ico" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"
      />
    </svg>
  );
}

function OdcIconClock() {
  return (
    <svg className="odc__dt-ico" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
      />
    </svg>
  );
}

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const APS = ['AM', 'PM'];

function OdcTimeWheel({ h12, m, ap, onChange }) {
  const hListRef = useRef(null);
  const mListRef = useRef(null);
  const pListRef = useRef(null);

  useLayoutEffect(() => {
    [hListRef.current, mListRef.current, pListRef.current].forEach((el) => {
      if (!el) return;
      el.querySelector('.odc-wheel__opt--on')?.scrollIntoView({ block: 'center' });
    });
  }, [h12, m, ap]);

  return (
    <div className="odc-wheel-row" role="group" aria-label="Time">
      <div className="odc-wheel">
        <ul ref={hListRef} className="odc-wheel__list" aria-label="Hour">
          {HOURS_12.map((h) => (
            <li key={h}>
              <button
                type="button"
                className={`odc-wheel__opt ${h12 === h ? 'odc-wheel__opt--on' : ''}`}
                onClick={() => onChange(toTime24(h, m, ap))}
              >
                {pad2(h)}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="odc-wheel">
        <ul ref={mListRef} className="odc-wheel__list" aria-label="Minute">
          {MINUTES.map((mm) => (
            <li key={mm}>
              <button
                type="button"
                className={`odc-wheel__opt ${m === mm ? 'odc-wheel__opt--on' : ''}`}
                onClick={() => onChange(toTime24(h12, mm, ap))}
              >
                {pad2(mm)}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="odc-wheel">
        <ul ref={pListRef} className="odc-wheel__list" aria-label="AM or PM">
          {APS.map((p) => (
            <li key={p}>
              <button
                type="button"
                className={`odc-wheel__opt odc-wheel__opt--ampm ${ap === p ? 'odc-wheel__opt--on' : ''}`}
                onClick={() => onChange(toTime24(h12, m, p))}
              >
                {p}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function OrderDetailsScheduleFields({ date, setDate, time, setTime, minDate }) {
  const dateLblId = useId();
  const timeLblId = useId();
  const popTitleId = useId();
  const [openPanel, setOpenPanel] = useState(null);
  const [view, setView] = useState(() => {
    const [y, mo] = minDate.split('-').map(Number);
    return new Date(y, mo - 1, 1);
  });

  useEffect(() => {
    if (!date || date < minDate) return;
    const [y, mo] = date.split('-').map(Number);
    const next = new Date(y, mo - 1, 1);
    setView((prev) => (prev.getTime() === next.getTime() ? prev : next));
  }, [date, minDate]);

  const showTime = Boolean(date && date >= minDate);

  useEffect(() => {
    if (!showTime && openPanel === 'time') setOpenPanel(null);
  }, [showTime, openPanel]);

  useEffect(() => {
    if (!openPanel) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenPanel(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openPanel]);

  const minParts = useMemo(() => {
    const [y, m] = minDate.split('-').map(Number);
    return { y, mo: m - 1 };
  }, [minDate]);

  const vy = view.getFullYear();
  const vm = view.getMonth();
  const firstDow = new Date(vy, vm, 1).getDay();
  const dim = new Date(vy, vm + 1, 0).getDate();
  const monthTitle = view.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ key: `b-${vy}-${vm}-${i}`, blank: true });
  for (let d = 1; d <= dim; d++) {
    const iso = `${vy}-${pad2(vm + 1)}-${pad2(d)}`;
    cells.push({ key: iso, iso, d, blank: false, disabled: iso < minDate });
  }

  const prevDisabled = vy < minParts.y || (vy === minParts.y && vm <= minParts.mo);

  const { h12, m: mm, ap } = parseTime24(time);

  const pickDate = (iso) => {
    setDate(iso);
    setOpenPanel(null);
  };

  const dateLabel = formatDateDisplay(date);
  const timeLabel = showTime ? formatTime12From24(time) : 'Select date first';

  const pickerPortal =
    openPanel &&
    createPortal(
      <div
        className="odc-dt-pop-overlay"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setOpenPanel(null);
        }}
      >
        <div
          className="odc-dt-pop-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby={popTitleId}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="odc-dt-pop-card__head">
            <h4 className="odc-dt-pop-card__title" id={popTitleId}>
              {openPanel === 'date' ? 'Select date' : 'Select time'}
            </h4>
            <button type="button" className="odc-dt-pop-card__close" onClick={() => setOpenPanel(null)} aria-label="Close">
              &times;
            </button>
          </div>
          <div className="odc-dt-pop-card__body">
            {openPanel === 'date' && (
              <div className="odc-cal odc-cal--pop">
                <div className="odc-cal__nav">
                  <button type="button" className="odc-cal__nav-btn" onClick={() => setView(new Date(vy, vm - 1, 1))} disabled={prevDisabled} aria-label="Previous month">
                    ‹
                  </button>
                  <span className="odc-cal__title">{monthTitle}</span>
                  <button type="button" className="odc-cal__nav-btn" onClick={() => setView(new Date(vy, vm + 1, 1))} aria-label="Next month">
                    ›
                  </button>
                </div>
                <div className="odc-cal__dow-row">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((w) => (
                    <span key={w} className="odc-cal__dow">
                      {w}
                    </span>
                  ))}
                </div>
                <div className="odc-cal__grid">
                  {cells.map((c) =>
                    c.blank ? (
                      <span key={c.key} className="odc-cal__pad" />
                    ) : (
                      <button
                        key={c.key}
                        type="button"
                        className={`odc-cal__day ${date === c.iso ? 'odc-cal__day--selected' : ''}`}
                        disabled={c.disabled}
                        onClick={() => pickDate(c.iso)}
                      >
                        {c.d}
                      </button>
                    )
                  )}
                </div>
                <div className="odc-cal__links">
                  <button type="button" className="odc-cal__link" onClick={() => pickDate(localTodayISO())}>
                    Today
                  </button>
                  <button
                    type="button"
                    className="odc-cal__link"
                    onClick={() => {
                      setDate('');
                      setOpenPanel(null);
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            {openPanel === 'time' && showTime && <OdcTimeWheel h12={h12} m={mm} ap={ap} onChange={setTime} />}
          </div>
          {openPanel === 'time' && showTime && (
            <div className="odc-dt-pop-card__foot">
              <button type="button" className="odc-dt-pop-card__done" onClick={() => setOpenPanel(null)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div className="odc__schedule-compact">
        <div className="odc__dt-inputs" role="group" aria-label="Schedule date and time">
          <div className="odc__dt-field">
            <span className="odc__dt-field-label" id={dateLblId}>
              Date
            </span>
            <button
              type="button"
              className={`odc__dt-trigger ${openPanel === 'date' ? 'odc__dt-trigger--open' : ''} ${!dateLabel ? 'odc__dt-trigger--placeholder' : ''}`}
              aria-expanded={openPanel === 'date'}
              aria-haspopup="dialog"
              aria-labelledby={dateLblId}
              onClick={() => setOpenPanel((p) => (p === 'date' ? null : 'date'))}
            >
              <OdcIconCalendar />
              <span className="odc__dt-trigger__value">{dateLabel || 'Select date'}</span>
            </button>
          </div>
          <div className="odc__dt-field">
            <span className="odc__dt-field-label" id={timeLblId}>
              Time
            </span>
            <button
              type="button"
              className={`odc__dt-trigger ${openPanel === 'time' ? 'odc__dt-trigger--open' : ''} ${!showTime ? 'odc__dt-trigger--placeholder' : ''}`}
              aria-expanded={openPanel === 'time'}
              aria-haspopup="dialog"
              aria-labelledby={timeLblId}
              disabled={!showTime}
              onClick={() => {
                if (!showTime) return;
                setOpenPanel((p) => (p === 'time' ? null : 'time'));
              }}
            >
              <OdcIconClock />
              <span className="odc__dt-trigger__value">{timeLabel}</span>
            </button>
          </div>
        </div>
      </div>
      {pickerPortal}
    </>
  );
}

function ensureGoogleMaps() {
  if (window.google?.maps?.Geocoder && window.google?.maps?.places?.AutocompleteService) {
    return Promise.resolve(window.google.maps);
  }
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!key) return Promise.reject(new Error('Missing Google Maps key'));

  if (!window.__odcGoogleMapsPromise) {
    window.__odcGoogleMapsPromise = new Promise((resolve, reject) => {
      const cb = `__odcGmInit_${Date.now()}`;
      window[cb] = () => {
        delete window[cb];
        resolve(window.google.maps);
      };
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&callback=${cb}`;
      s.async = true;
      s.onerror = () => {
        delete window[cb];
        window.__odcGoogleMapsPromise = null;
        reject(new Error('Google Maps script failed'));
      };
      document.head.appendChild(s);
    });
  }
  return window.__odcGoogleMapsPromise;
}

export default function OrderDetailsCard({ mode, onModeChange, onClose }) {
  const { currentAddress, setCurrentAddress } = useDeliveryAddress();

  const [query, setQuery] = useState(() =>
    mode === 'delivery' && currentAddress?.address ? currentAddress.address : ''
  );
  const [suggestions, setSuggestions] = useState([]);
  const [draftAddress, setDraftAddress] = useState(() =>
    mode === 'delivery' && currentAddress?.address ? currentAddress : null
  );
  const [timeOption, setTimeOption] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const cardRef = useRef(null);
  const scheduleFieldsRef = useRef(null);

  useEffect(() => {
    if (timeOption !== 'schedule') return;
    const today = localTodayISO();
    setScheduledDate((d) => {
      if (!d || d < today) return today;
      return d;
    });
    setScheduledTime((t) => normalizeTimeString(t || '12:00'));
    requestAnimationFrame(() => {
      scheduleFieldsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }, [timeOption]);


  const searchAddress = useCallback(async (input) => {
    const trimmed = String(input || '').trim();
    if (trimmed.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const maps = await ensureGoogleMaps();
      if (!autocompleteRef.current) {
        autocompleteRef.current = new maps.places.AutocompleteService();
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
      }
      autocompleteRef.current.getPlacePredictions(
        { input: trimmed, types: ['geocode'], sessionToken: sessionTokenRef.current },
        (results, status) => {
          if (status === 'OK' || status === maps.places.PlacesServiceStatus.OK) {
            setSuggestions(
              (results || []).map((p) => ({
                placeId: p.place_id,
                description: p.description,
                mainText: p.structured_formatting?.main_text || '',
                secondaryText: p.structured_formatting?.secondary_text || '',
              }))
            );
          } else {
            setSuggestions([]);
          }
        }
      );
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleQueryChange = useCallback(
    (e) => {
      const val = e.target.value;
      setQuery(val);
      setDraftAddress(null);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchAddress(val), 300);
    },
    [searchAddress]
  );

  const selectSuggestion = useCallback(
    async (suggestion) => {
      setQuery(suggestion.description);
      setSuggestions([]);
      try {
        const maps = await ensureGoogleMaps();
        if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();
        sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
        geocoderRef.current.geocode({ placeId: suggestion.placeId }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            const loc = results[0].geometry.location;
            setDraftAddress({
              address: suggestion.description,
              coords: { latitude: loc.lat(), longitude: loc.lng() },
            });
          } else {
            setDraftAddress({ address: suggestion.description, coords: null });
          }
        });
      } catch {
        setDraftAddress({ address: suggestion.description, coords: null });
      }
    },
    []
  );

  const handleSave = useCallback(() => {
    if (mode === 'delivery' && draftAddress) {
      setCurrentAddress({ ...draftAddress, source: 'manual' });
    }
    onClose();
  }, [mode, draftAddress, setCurrentAddress, onClose]);

  const handleSwitch = useCallback(() => {
    const next = mode === 'delivery' ? 'pickup' : 'delivery';
    onModeChange(next);
    setSuggestions([]);
    setDraftAddress(null);
    if (next === 'delivery' && currentAddress?.address) {
      setQuery(currentAddress.address);
      setDraftAddress(currentAddress);
    } else {
      setQuery('');
    }
  }, [mode, onModeChange, currentAddress]);

  const canSave = mode === 'pickup' || (mode === 'delivery' && !!draftAddress);

  const scheduleMinDate = localTodayISO();

  const timeScheduleSection = (heading) => (
    <div className="odc__section">
      <div className="odc__section-label">
        <span className="odc__clock-icon">&#9201;</span>
        {heading}
      </div>
      <div className="odc__time-toggle">
        <button
          type="button"
          className={`odc__time-btn ${timeOption === 'now' ? 'odc__time-btn--active' : ''}`}
          onClick={() => setTimeOption('now')}
        >
          Now
        </button>
        <button
          type="button"
          className={`odc__time-btn ${timeOption === 'schedule' ? 'odc__time-btn--active' : ''}`}
          onClick={() => setTimeOption('schedule')}
        >
          Schedule ahead
        </button>
      </div>
      {timeOption === 'schedule' && (
        <div ref={scheduleFieldsRef} className="odc__schedule-fields">
          <OrderDetailsScheduleFields
            date={scheduledDate}
            setDate={setScheduledDate}
            time={scheduledTime}
            setTime={setScheduledTime}
            minDate={scheduleMinDate}
          />
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className="odc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="odc" ref={cardRef}>
        <div className="odc__head">
          <h3 className="odc__title">Order details</h3>
          <button type="button" className="odc__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="odc__body">
          {mode === 'pickup' ? (
            <>
              <div className="odc__section">
                <div className="odc__section-label">
                  <img src={locationIcon} alt="" className="odc__section-icon" />
                  Pickup address
                </div>
                <p className="odc__pickup-addr">
                  {currentAddress?.address || 'Restaurant pickup address'}
                </p>
              </div>

              {timeScheduleSection('Pickup time')}
            </>
          ) : (
            <>
              <div className="odc__section">
                <div className="odc__section-label">
                  <img src={locationIcon} alt="" className="odc__section-icon" />
                  Delivery address
                </div>
                <div className="odc__search-wrap">
                  <input
                    type="text"
                    className="odc__search"
                    placeholder="Search for an address"
                    value={query}
                    onChange={handleQueryChange}
                    autoComplete="off"
                  />
                  <span className="odc__search-icon" aria-hidden="true">&#128269;</span>
                </div>
                {suggestions.length > 0 && (
                  <ul className="odc__suggestions">
                    {suggestions.map((s) => (
                      <li key={s.placeId}>
                        <button
                          type="button"
                          className="odc__suggestion-btn"
                          onClick={() => selectSuggestion(s)}
                        >
                          <span className="odc__suggestion-main">{s.mainText}</span>
                          <span className="odc__suggestion-secondary">{s.secondaryText}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>

        <div className="odc__foot">
          <button type="button" className="odc__switch-btn" onClick={handleSwitch}>
            Switch to {mode === 'delivery' ? 'pickup' : 'delivery'}
          </button>
          <button
            type="button"
            className="odc__save-btn"
            onClick={handleSave}
            disabled={!canSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
